import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtPrice, fmtPct, changeColor } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowUpRight } from "lucide-react";

export default function Bundle() {
  const { key } = useParams();
  const [bundle, setBundle] = useState(null);

  useEffect(() => {
    api.get(`/bundles/${key}`).then(({data}) => setBundle(data));
    const id = setInterval(() => api.get(`/bundles/${key}`).then(({data}) => setBundle(data)), 20000);
    return () => clearInterval(id);
  }, [key]);

  if (!bundle) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const movers = [...bundle.quotes].sort((a,b) => Math.abs(b.change_pct) - Math.abs(a.change_pct)).slice(0,3);
  const avgChange = bundle.quotes.reduce((s, q) => s + (q.change_pct || 0), 0) / bundle.quotes.length;

  return (
    <div className="space-y-6" data-testid={`bundle-${key}`}>
      <div>
        <Link to="/app/markets" className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary">← Markets</Link>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">{bundle.label}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{bundle.quotes.length} commodities · basket weighted 24h move <span className={`num font-semibold ${changeColor(avgChange)}`}>{fmtPct(avgChange)}</span></p>
      </div>

      {/* movers */}
      <div className="grid gap-3 sm:grid-cols-3">
        {movers.map(q => (
          <Link
            key={q.code}
            to={`/commodity/${q.code}`}
            data-testid={`bundle-mover-${q.code.toLowerCase()}`}
            className="rounded-lg border border-border/60 bg-card/60 p-4 transition hover:border-primary/40"
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Top mover</span>
              <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="mt-2 font-display text-base font-semibold">{q.name}</div>
            <div className="mt-2 num text-2xl font-semibold">{fmtPrice(q.price)}</div>
            <div className={`text-xs font-mono ${changeColor(q.change_pct)}`}>{fmtPct(q.change_pct)} · {q.unit}</div>
          </Link>
        ))}
      </div>

      {/* full grid */}
      <Card className="border-border/60 bg-card/60">
        <CardHeader><CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground">Basket · full</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="term-scroll overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                <tr className="border-b border-border/60">
                  <th className="px-4 py-3 text-left">Commodity</th>
                  <th className="px-4 py-3 text-right">Price</th>
                  <th className="px-4 py-3 text-right">24h</th>
                  <th className="px-4 py-3 text-right">Range</th>
                  <th className="hidden px-4 py-3 text-right sm:table-cell">Feed</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {bundle.quotes.map(q => (
                  <tr key={q.code} data-testid={`bundle-row-${q.code.toLowerCase()}`} className="transition hover:bg-secondary/30">
                    <td className="px-4 py-3">
                      <Link to={`/commodity/${q.code}`} className="font-semibold hover:text-primary">{q.name}</Link>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{q.code} · {q.unit}</div>
                    </td>
                    <td className="px-4 py-3 text-right num font-semibold">{fmtPrice(q.price)}</td>
                    <td className={`px-4 py-3 text-right font-mono ${changeColor(q.change_pct)}`}>{fmtPct(q.change_pct)}</td>
                    <td className="px-4 py-3 text-right num text-muted-foreground">{fmtPrice(q.low)} – {fmtPrice(q.high)}</td>
                    <td className="hidden px-4 py-3 text-right sm:table-cell">
                      <span className={`text-[10px] font-mono uppercase tracking-widest ${q.feed_source==="yfinance" ? "text-[hsl(var(--signal-buy))]" : "text-muted-foreground"}`}>{q.feed_source === "yfinance" ? "LIVE" : "USDA"}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/commodity/${q.code}`}><Button size="sm" variant="outline" data-testid={`bundle-open-${q.code.toLowerCase()}`} className="rounded-full">Open</Button></Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
